import { type FormEvent, useEffect, useRef, useState } from 'react';
import {
  FreebeImportScheduleApiError,
  freebeImportScheduleApi,
  type FreebeImportScheduleResult,
  type FreebeImportWeekday,
} from './freebe-import-schedule-api';
import { PrototypeIcon } from './PrototypeIcon';

interface FreebeImportScheduleCardProps {
  readonly csrfToken: string;
  readonly onScheduledRunDue?: () => Promise<void> | void;
}

const SCHEDULE_WAKEUP_WINDOW_MS = 60_000;
const SCHEDULE_REFRESH_INTERVAL_MS = 10_000;
const MAXIMUM_LOCAL_TIMER_MS = 6 * 60 * 60_000;
const WEEKDAYS: readonly { value: FreebeImportWeekday; label: string }[] = [
  { value: 'MONDAY', label: 'Lun' },
  { value: 'TUESDAY', label: 'Mar' },
  { value: 'WEDNESDAY', label: 'Mer' },
  { value: 'THURSDAY', label: 'Jeu' },
  { value: 'FRIDAY', label: 'Ven' },
  { value: 'SATURDAY', label: 'Sam' },
  { value: 'SUNDAY', label: 'Dim' },
];

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'full',
  timeStyle: 'short',
  timeZone: 'Europe/Paris',
});

export function FreebeImportScheduleCard({
  csrfToken,
  onScheduledRunDue,
}: FreebeImportScheduleCardProps): React.JSX.Element {
  const [schedule, setSchedule] = useState<FreebeImportScheduleResult | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [localTime, setLocalTime] = useState('06:00');
  const [activeWeekdays, setActiveWeekdays] = useState<readonly FreebeImportWeekday[]>([
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scheduleWakeup, setScheduleWakeup] = useState(0);
  const dueCallback = useRef(onScheduledRunDue);

  useEffect(() => {
    dueCallback.current = onScheduledRunDue;
  }, [onScheduledRunDue]);

  function apply(result: FreebeImportScheduleResult): void {
    setSchedule(result);
    setEnabled(result.enabled);
    setLocalTime(result.localTime);
    setActiveWeekdays(
      result.activeWeekdays ?? ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
    );
  }

  useEffect(() => {
    let active = true;
    void freebeImportScheduleApi
      .read()
      .then((result) => {
        if (active) apply(result);
      })
      .catch(() => {
        if (active) setError('La planification des mises à jour ne peut pas être chargée.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (schedule?.enabled !== true || schedule.nextRunAt === null) return;
    const dueAt = new Date(schedule.nextRunAt).getTime();
    if (!Number.isFinite(dueAt)) return;

    const millisecondsUntilDue = dueAt - Date.now();
    if (millisecondsUntilDue > SCHEDULE_WAKEUP_WINDOW_MS) {
      const timer = window.setTimeout(
        () => setScheduleWakeup((value) => value + 1),
        Math.min(millisecondsUntilDue - SCHEDULE_WAKEUP_WINDOW_MS, MAXIMUM_LOCAL_TIMER_MS),
      );
      return () => window.clearTimeout(timer);
    }

    let active = true;
    const refreshDueRun = (): void => {
      void freebeImportScheduleApi
        .read()
        .then((result) => {
          if (active) setSchedule(result);
        })
        .catch(() => undefined);
      void Promise.resolve(dueCallback.current?.()).catch(() => undefined);
    };
    refreshDueRun();
    const timer = window.setInterval(refreshDueRun, SCHEDULE_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [schedule?.enabled, schedule?.nextRunAt, scheduleWakeup]);

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    setError(null);
    try {
      const result = await freebeImportScheduleApi.save(
        {
          enabled,
          localTime,
          timeZone: 'Europe/Paris',
          activeWeekdays,
        },
        csrfToken,
      );
      apply(result);
      setNotice(
        result.enabled
          ? 'Planification enregistrée. La prochaine mise à jour est maintenant programmée.'
          : 'Planification désactivée. Les jours et l’heure restent mémorisés.',
      );
    } catch (caught) {
      if (
        caught instanceof FreebeImportScheduleApiError &&
        caught.code === 'FREEBE_AUTHORIZATION_REQUIRED'
      ) {
        setError('Finalisez la connexion Freebe avant d’activer la planification.');
      } else if (
        caught instanceof FreebeImportScheduleApiError &&
        caught.code === 'FREEBE_SCHEDULE_INVALID'
      ) {
        setError('Choisissez une heure et au moins un jour d’exécution valides.');
      } else {
        setError('La planification ne peut pas être enregistrée pour le moment.');
      }
    } finally {
      setSaving(false);
    }
  }

  const canEdit = schedule?.accountConfigured === true;
  const canEnable = schedule?.canEnable === true;
  const activationLocked = !canEnable && !schedule?.enabled;

  function showFreebeConnection(): void {
    document
      .getElementById('freebe-connection-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section
      className="freebe-integration__schedule"
      aria-labelledby="freebe-schedule-title"
      aria-busy={loading || saving}
    >
      <header>
        <div>
          <p className="dashboard-panel__eyebrow">Automatisation Freebe</p>
          <h2 id="freebe-schedule-title">Planification des mises à jour</h2>
        </div>
        <span className={schedule?.enabled ? 'is-enabled' : undefined}>
          {schedule?.enabled ? 'Activée' : 'Désactivée'}
        </span>
      </header>

      {error ? (
        <p className="freebe-integration__schedule-message tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="freebe-integration__schedule-message tosho-feedback is-success" role="status">
          {notice}
        </p>
      ) : null}

      <div className="freebe-integration__schedule-layout">
        <form onSubmit={(event) => void save(event)}>
          <label className="freebe-integration__schedule-toggle">
            <input
              type="checkbox"
              checked={enabled}
              disabled={loading || saving || activationLocked || !canEdit}
              onChange={(event) => setEnabled(event.currentTarget.checked)}
            />
            <span>
              <strong>Activer les mises à jour automatiques</strong>
              <small>
                À chaque échéance, Tosho met à jour Clients, Contacts, Fournisseurs, Factures
                d’achat, Factures de vente, Devis, Opérations puis Mouvements, successivement.
              </small>
            </span>
          </label>

          {activationLocked && canEdit ? (
            <div className="freebe-integration__schedule-locked" role="note">
              <p>
                <strong>Activation indisponible</strong>
                <span>
                  Reconnectez Freebe et laissez Tosho vérifier la session pour activer la
                  planification.
                </span>
              </p>
              <button type="button" onClick={showFreebeConnection}>
                Reconnecter Freebe
              </button>
            </div>
          ) : null}

          <div className="freebe-integration__schedule-fields">
            <label>
              <span>Heure de lancement</span>
              <input
                type="time"
                step={60}
                required
                value={localTime}
                disabled={loading || saving || !canEdit}
                onChange={(event) => setLocalTime(event.currentTarget.value)}
              />
            </label>
            <div>
              <span>Fuseau horaire</span>
              <strong>Europe/Paris</strong>
            </div>
          </div>

          <fieldset className="freebe-integration__schedule-weekdays">
            <legend>Jours d’exécution</legend>
            <div>
              {WEEKDAYS.map((weekday) => (
                <label key={weekday.value}>
                  <input
                    type="checkbox"
                    checked={activeWeekdays.includes(weekday.value)}
                    disabled={loading || saving || !canEdit}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setActiveWeekdays((current) =>
                        checked
                          ? [...current, weekday.value]
                          : current.filter((day) => day !== weekday.value),
                      );
                    }}
                  />
                  <span>{weekday.label}</span>
                </label>
              ))}
            </div>
            <small>Du lundi au vendredi par défaut. Samedi et dimanche sont exclus.</small>
          </fieldset>

          <div className="freebe-integration__schedule-next">
            <PrototypeIcon name="history" />
            <p>
              <span>Prochaine exécution</span>
              <strong>{formatNextRun(schedule?.nextRunAt ?? null)}</strong>
            </p>
          </div>

          <p className="freebe-integration__schedule-help">
            Pour un premier test, choisissez une heure située dans les trente prochaines minutes.
            Tosho réutilisera la période annuelle proposée par Freebe sans modifier son calendrier.
          </p>

          <button
            type="submit"
            disabled={loading || saving || !canEdit || activeWeekdays.length === 0}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer la planification'}
          </button>
        </form>

        <aside aria-label="Prérequis de la planification Freebe">
          <h3>Prérequis</h3>
          <ScheduleRequirement
            valid={schedule?.accountConfigured === true}
            label="Compte Freebe renseigné"
          />
          <ScheduleRequirement
            valid={schedule?.canEnable === true}
            label="Autorisation Freebe enregistrée"
          />
          <ScheduleRequirement valid={null} label="Session chiffrée contrôlée à l’exécution" />
          <ScheduleRequirement valid={schedule?.enabled === true} label="Planification activée" />
          <ScheduleRequirement valid={null} label="Worker et file Redis contrôlés à l’exécution" />
          <ScheduleRequirement
            valid={null}
            label="Une seule séquence d’import autorisée à la fois"
          />
          <p>
            Une session expirée, une réauthentification ou un écran Freebe inattendu interrompt le
            traitement et laisse une tentative visible dans l’historique.
          </p>
        </aside>
      </div>
    </section>
  );
}

function ScheduleRequirement({
  valid,
  label,
}: {
  readonly valid: boolean | null;
  readonly label: string;
}): React.JSX.Element {
  return (
    <div className="freebe-integration__schedule-requirement">
      <PrototypeIcon name={valid === true ? 'shield-check' : 'circle-help'} />
      <span>{label}</span>
      <strong>{valid === null ? 'Contrôle automatique' : valid ? 'OK' : 'À finaliser'}</strong>
    </div>
  );
}

function formatNextRun(value: string | null): string {
  if (value === null) return 'Aucune exécution programmée';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? dateFormatter.format(date) : 'Date indisponible';
}
